import { EditableAccessRule, PersistedAccessRule } from "../models";

export type AccessRuleDiff<TCreated, TUpdated, TDeleted> = {
  created: TCreated[];
  updated: TUpdated[];
  deleted: TDeleted[];
};

/**
 * Re-attaches persisted IDs to current rules when identity matches.
 * This collapses delete → re-add into a no-op or update.
 */
function normalizeCurrentRules<
  TPersisted extends PersistedAccessRule,
  TEditable extends EditableAccessRule,
>(
  original: TPersisted[],
  current: TEditable[],
  getIdentityKey?: (entity: TEditable | TPersisted) => string,
): TEditable[] {
  if (!getIdentityKey) {
    return current;
  }

  const originalByIdentity = new Map(
    original.map(o => [getIdentityKey(o), o]),
  );

  const byIdentity = new Map<string, TEditable>();

  for (const rule of current) {
    const identity = getIdentityKey(rule);

    // Reattach ID if this identity exists in persisted data)
    const persisted = originalByIdentity.get(identity);
    const normalizedRule =
      persisted && rule.liftAccessRuleId == null
        ? { ...rule, liftAccessRuleId: persisted.liftAccessRuleId }
        : rule;

    const existing = byIdentity.get(identity);

    // dedupe with priority
    if (!existing) {
      byIdentity.set(identity, normalizedRule);
      continue;
    }

    // Persisted rule wins
    if (
      existing.liftAccessRuleId == null &&
      normalizedRule.liftAccessRuleId != null
    ) {
      byIdentity.set(identity, normalizedRule);
    }
  }

  return Array.from(byIdentity.values());
}

/**
 * Computes a minimal diff between persisted access rules and the current
 * edited rules.
 *
 * if a rule is deleted and then re-added with the same identity, the delete
 * is treated as an undo rather than a create + delete pair.
 *
 * Identity-based reconciliation happens BEFORE diffing so that:
 * - delete → re-add same rule = no change
 * - delete → re-add modified rule = update
 *
 * For rule types where only one rule can exist, identity is not included
 * (MaxDays, EffectiveDateRange)
 * 
 * Add new rule                   - created
 * Edit existing rule             - updated
 * Delete rule                    - deleted
 * Delete - re-add same rule      - no change
 * Delete - re-add modified rule  - updated
 * Delete - add different rule    - deleted + created
 *
 **/
export function diffEditableAccessRules<
  TPersisted extends PersistedAccessRule,
  TEditable extends EditableAccessRule,
>(
  original: TPersisted[],
  current: TEditable[],
  options: {
    isDeleted: (entity: TEditable) => boolean;
    isUpdated: (original: TPersisted, current: TEditable) => boolean;
    getIdentityKey?: (entity: TEditable | TPersisted) => string;
  },
): AccessRuleDiff<TEditable, TEditable, TPersisted> {
  const { isDeleted, isUpdated, getIdentityKey } = options;

  const originalById = new Map(original.map((o) => [o.liftAccessRuleId, o]));

  const normalizedCurrent = normalizeCurrentRules(
    original,
    current,
    getIdentityKey,
  );

  const created: TEditable[] = [];
  const updated: TEditable[] = [];
  const deleted: TPersisted[] = [];

  const seenIds = new Set<number>();

  for (const rule of normalizedCurrent) {
    const id = rule.liftAccessRuleId;

    if (id == null) {
      if (!isDeleted(rule)) {
        created.push(rule);
      }
      continue;
    }

    seenIds.add(id);
    const originalRule = originalById.get(id);
    if (!originalRule) continue;

    if (isDeleted(rule)) {
      deleted.push(originalRule);
    } else if (isUpdated(originalRule, rule)) {
      updated.push(rule);
    }
  }

  for (const originalRule of original) {
    if (!seenIds.has(originalRule.liftAccessRuleId)) {
      deleted.push(originalRule);
    }
  }

  return { created, updated, deleted };
}
